

import React, { useState, useEffect, useRef } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import HotelDmcTabs from "./HotelDmcTabs";
import roomTypesData from "../../Data/RoomTypes.json";

const HotelDMCProperties = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    // State for saved hotels
    const [savedHotels, setSavedHotels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [showHotelDetails, setShowHotelDetails] = useState(false);
    const [activeTab, setActiveTab] = useState("roomDetails");
    
    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    
    // Preview state
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    
    // Description expand state for preview modal
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    
    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState("");
    
    // Global validity dates for all room types
    const [globalValidFrom, setGlobalValidFrom] = useState("");
    const [globalValidTo, setGlobalValidTo] = useState("");
    
    // Room details state
    const [roomDetails, setRoomDetails] = useState([]);
    const [showAddRoomForm, setShowAddRoomForm] = useState(false);
    const [newRoom, setNewRoom] = useState({
        roomType: "",
        mealPlan: "CP",
        extraBed: false,
        extraBedPrice: "",
        childWithoutBed: false,
        childWithoutBedPrice: "",
        price: "",
        currency: "INR"
    });

    // Room type options from JSON
    const roomTypeOptions = roomTypesData.roomTypes || [];
    
    // Auto-complete states for room type
    const [roomTypeSuggestions, setRoomTypeSuggestions] = useState([]);
    const [showRoomTypeDropdown, setShowRoomTypeDropdown] = useState(false);
    const [roomTypeSearchTerm, setRoomTypeSearchTerm] = useState("");
    const roomTypeInputRef = useRef(null);

    // Meal plan options
    const mealPlanOptions = [
        { value: "CP", label: "CP → Breakfast only" },
        { value: "MAP", label: "MAP → Breakfast + Lunch or Dinner" },
        { value: "AP", label: "AP → Breakfast + Lunch + Dinner" },
        { value: "EP", label: "EP → Room only" },
        { value: "CPAI", label: "CPAI → Stay + Breakfast" },
        { value: "MAPAI", label: "MAPAI → Stay + Breakfast + Lunch/Dinner" },
        { value: "APAI", label: "APAI → Stay + Breakfast + Lunch + Dinner" }
    ];

    // Currency options
    const currencyOptions = [
        { code: "USD", symbol: "$", name: "US Dollar" },
        { code: "EUR", symbol: "€", name: "Euro" },
        { code: "GBP", symbol: "£", name: "British Pound" },
        { code: "INR", symbol: "₹", name: "Indian Rupee" },
        { code: "AED", symbol: "AED", name: "UAE Dirham" },
        { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
        { code: "AUD", symbol: "A$", name: "Australian Dollar" },
        { code: "CAD", symbol: "C$", name: "Canadian Dollar" }
    ];

    // Amenities state
    const [amenities, setAmenities] = useState([]);
    const [newAmenityPoint, setNewAmenityPoint] = useState("");

    // Terms & Conditions state
    const [termsAndConditions, setTermsAndConditions] = useState([]);
    const [newTermPoint, setNewTermPoint] = useState("");

    // Cancellation Policy state
    const [cancellationPolicy, setCancellationPolicy] = useState([]);
    const [newCancellationPoint, setNewCancellationPoint] = useState("");

    // Child Policy state
    const [childPolicy, setChildPolicy] = useState([]);
    const [newChildPolicyPoint, setNewChildPolicyPoint] = useState("");

    // Bank Details state
    const [bankDetails, setBankDetails] = useState([]);
    const [showAddBankForm, setShowAddBankForm] = useState(false);
    const [newBankDetail, setNewBankDetail] = useState({
        bankName: "",
        accountNumber: "",
        swiftIfscCode: ""
    });

    // Contact details state
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    const [newContact, setNewContact] = useState({
        contactName: "",
        email: "",
        phone: "",
        whatsApp: ""
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage] = useState(12);

    const searchInputRef = useRef(null);
    const resultsRef = useRef(null);
    const searchDropdownRef = useRef(null);

    // Close room type dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (roomTypeInputRef.current && !roomTypeInputRef.current.contains(event.target) && 
                !event.target.closest('.room-type-dropdown')) {
                setShowRoomTypeDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Fetch saved hotels on component mount and when search term changes
    useEffect(() => {
        if (user && !loading) {
            fetchSavedHotels();
        }
    }, [user, loading]);

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            if (user && !loading) {
                fetchSavedHotels();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // API call to get saved hotels with search
    const fetchSavedHotels = async () => {
        setIsLoading(true);
        setIsSearching(searchTerm.length >= 3);
        setError(null);
        
        try {
            // Build query parameters
            let queryParams = `?userId=${user.id}`;
            
            // Add search term if provided and has at least 3 characters
            if (searchTerm && searchTerm.trim().length >= 3) {
                queryParams += `&searchTerm=${encodeURIComponent(searchTerm.trim())}`;
            }
            
            const response = await apiService.getAll(
                `${config.GETCOMPANYHOTELS}${queryParams}`
            );
            
            const hotels = response.hotels || response.data || [];
            setSavedHotels(hotels);
            
            // Reset to first page when search results change
            setCurrentPage(1);
        } catch (error) {
            console.error("Error fetching saved hotels:", error);
            setError("Failed to load saved hotels. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-complete handler for room type
    const handleRoomTypeSearch = (searchTerm) => {
        setRoomTypeSearchTerm(searchTerm);
        if (searchTerm.trim().length > 0) {
            const filtered = roomTypeOptions.filter(type =>
                type.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setRoomTypeSuggestions(filtered);
            setShowRoomTypeDropdown(true);
        } else {
            setRoomTypeSuggestions([]);
            setShowRoomTypeDropdown(false);
        }
    };

    // Select room type from suggestions
    const selectRoomType = (roomType) => {
        setNewRoom(prev => ({ ...prev, roomType }));
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setShowRoomTypeDropdown(false);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Handle search form submission
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchSavedHotels();
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm("");
        fetchSavedHotels();
    };

    // API call to save/update hotel
    const saveHotelDetails = async () => {
        if (!selectedHotel || !user) {
            setSaveError("No hotel selected or user not authenticated");
            return;
        }

        setIsSaving(true);
        setSaveError("");
        setSaveSuccess(false);
        
        try {
            const dto = {
                hotelId: selectedHotel.hotelId || selectedHotel.HotelId,
                isOwner: true,
                contactInfo: {
                    contactName: selectedHotel.contactInfo?.contactName || newContact.contactName || user?.name || "",
                    email: selectedHotel.contactInfo?.email || newContact.email || user?.email || "",
                    phone: selectedHotel.contactInfo?.phone || newContact.phone || user?.phoneNumber || "",
                    whatsApp: selectedHotel.contactInfo?.whatsApp || newContact.whatsApp || user?.whatsAppNumber || ""
                },
                roomDetails: roomDetails.map(room => ({
                    roomType: room.roomType,
                    mealPlan: room.mealPlan,
                    extraBed: room.extraBed || false,
                    extraBedPrice: room.extraBed ? (parseFloat(room.extraBedPrice) || 0) : null,
                    childWithoutBed: room.childWithoutBed || false,
                    childWithoutBedPrice: room.childWithoutBed ? (parseFloat(room.childWithoutBedPrice) || 0) : null,
                    price: parseFloat(room.price) || 0,
                    currency: room.currency,
                    validFrom: globalValidFrom || null,
                    validTo: globalValidTo || null
                })),
                amenities: amenities.map(amenity => ({
                    point: amenity.point
                })),
                termsAndConditions: termsAndConditions.map(term => ({
                    point: term.point
                })),
                cancellationPolicy: cancellationPolicy.map(policy => ({
                    point: policy.point
                })),
                childPolicy: childPolicy.map(policy => ({
                    point: policy.point
                })),
                bankDetails: bankDetails.map(bank => ({
                    bankName: bank.bankName,
                    accountNumber: bank.accountNumber,
                    swiftIfscCode: bank.swiftIfscCode || ""
                }))
            };

            const request = {
                userId: user.id,
                companyId: user.cid,
                dto: dto
            };

            const response = await apiService.post(`${config.DMCHOTELS}company-hotels/save`, request);
            
            if (response && response.success) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
                
                // Refresh the list with current search term
                fetchSavedHotels();
                
                // Update selected hotel with new data
                if (response.data) {
                    setSelectedHotel(prev => ({
                        ...prev,
                        ...response.data,
                        contactInfo: dto.contactInfo,
                        roomDetails: dto.roomDetails,
                        amenities: dto.amenities,
                        termsAndConditions: dto.termsAndConditions,
                        cancellationPolicy: dto.cancellationPolicy,
                        childPolicy: dto.childPolicy,
                        bankDetails: dto.bankDetails
                    }));
                }
            } else {
                setSaveError(response.message || "Failed to save hotel details");
            }
        } catch (error) {
            console.error("Error saving hotel details:", error);
            setSaveError(error.response?.data?.message || error.message || "Error saving hotel details");
        } finally {
            setIsSaving(false);
        }
    };

    // API call to remove hotel
    const removeHotel = async (hotelId) => {
        if (!window.confirm("Are you sure you want to remove this hotel from your list?")) {
            return;
        }
        
        try {
            await apiService.delete(
                `${config.DMCHOTELS}company-hotels/remove/${hotelId}?userId=${user.id}`
            );
            
            // Refresh the list with current search term
            fetchSavedHotels();
            
            // If the removed hotel is currently selected, clear selection
            if (selectedHotel && selectedHotel.hotelId === hotelId) {
                setSelectedHotel(null);
                setShowHotelDetails(false);
            }
            
            alert("Hotel removed successfully!");
        } catch (error) {
            console.error("Error removing hotel:", error);
            alert("Failed to remove hotel. Please try again.");
        }
    };

    // Handle hotel selection
    const handleHotelSelect = (hotel) => {
        setSelectedHotel(hotel);
        setShowHotelDetails(true);
        setSaveSuccess(false);
        setSaveError("");
        
        // Load existing details if available
        setRoomDetails(hotel.roomDetails || []);
        setAmenities(hotel.amenities || []);
        setTermsAndConditions(hotel.termsAndConditions || []);
        setCancellationPolicy(hotel.cancellationPolicy || []);
        setChildPolicy(hotel.childPolicy || []);
        setBankDetails(hotel.bankDetails || []);
        
        // FIXED: Extract and set global validity dates from the first room
        if (hotel.roomDetails && hotel.roomDetails.length > 0) {
            const firstRoom = hotel.roomDetails[0];
            
            // Format dates for input fields (YYYY-MM-DD)
            if (firstRoom.validFrom) {
                try {
                    const date = new Date(firstRoom.validFrom);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setGlobalValidFrom(`${year}-${month}-${day}`);
                } catch (e) {
                    console.error("Error parsing validFrom date:", e);
                    setGlobalValidFrom("");
                }
            } else {
                setGlobalValidFrom("");
            }
            
            if (firstRoom.validTo) {
                try {
                    const date = new Date(firstRoom.validTo);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setGlobalValidTo(`${year}-${month}-${day}`);
                } catch (e) {
                    console.error("Error parsing validTo date:", e);
                    setGlobalValidTo("");
                }
            } else {
                setGlobalValidTo("");
            }
        } else {
            setGlobalValidFrom("");
            setGlobalValidTo("");
        }
        
        // Set initial contact form values
        setNewContact({
            contactName: hotel.contactInfo?.contactName || user?.name || "",
            email: hotel.contactInfo?.email || user?.email || "",
            phone: hotel.contactInfo?.phone || user?.phoneNumber || "",
            whatsApp: hotel.contactInfo?.whatsApp || user?.whatsAppNumber || ""
        });
        
        setActiveTab("roomDetails");
        setRoomTypeSearchTerm("");
        
        // Scroll to hotel details
        setTimeout(() => {
            const detailsSection = document.getElementById('hotel-details-section');
            if (detailsSection) {
                detailsSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    // Close hotel details
    const closeHotelDetails = () => {
        setShowHotelDetails(false);
        setSelectedHotel(null);
        resetForms();
    };

    // Reset all forms
    const resetForms = () => {
        setRoomDetails([]);
        setAmenities([]);
        setTermsAndConditions([]);
        setCancellationPolicy([]);
        setChildPolicy([]);
        setBankDetails([]);
        setShowAddRoomForm(false);
        setShowAddContactForm(false);
        setShowAddBankForm(false);
        setActiveTab("roomDetails");
        setNewAmenityPoint("");
        setNewTermPoint("");
        setNewCancellationPoint("");
        setNewChildPolicyPoint("");
        setGlobalValidFrom("");
        setGlobalValidTo("");
        setNewRoom({
            roomType: "",
            mealPlan: "CP",
            extraBed: false,
            extraBedPrice: "",
            childWithoutBed: false,
            childWithoutBedPrice: "",
            price: "",
            currency: "INR"
        });
        setNewContact({
            contactName: "",
            email: "",
            phone: "",
            whatsApp: ""
        });
        setNewBankDetail({
            bankName: "",
            accountNumber: "",
            swiftIfscCode: ""
        });
        setRoomTypeSearchTerm("");
        setRoomTypeSuggestions([]);
        setSaveSuccess(false);
        setSaveError("");
        setIsDescriptionExpanded(false);
    };

    // Share via WhatsApp Web
    const shareViaWhatsApp = () => {
        if (!selectedHotel) return;

        const hotelName = selectedHotel.hotelName || "Hotel";
        const stars = selectedHotel.starRating || selectedHotel.starCategory || "";
        const accommodationType = selectedHotel.accommodationType || selectedHotel.accommdationType || "Hotel";
        
        const address = selectedHotel.address || "";
        const city = selectedHotel.city || "";
        const state = selectedHotel.state || "";
        const country = selectedHotel.country || "";
        
        const contactName = selectedHotel.contactInfo?.contactName || user?.name || "";
        const phone = selectedHotel.contactInfo?.phone || user?.phoneNumber || "";
        const whatsapp = selectedHotel.contactInfo?.whatsApp || user?.whatsAppNumber || "";
        const email = selectedHotel.contactInfo?.email || user?.email || "";
        
        const latitude = selectedHotel.latitude || "";
        const longitude = selectedHotel.longitude || "";

        let message = `Dear Agents,\n\n`;
        message += `Hope you are doing well.\n\n`;
        message += `✨ *Attractive Offers Available*\n\n`;
        message += `🏨 *${hotelName}*`;
        if (stars) message += ` (${stars}⭐ ${accommodationType})`;
        message += `\n\n`;
        
        if (roomDetails.length > 0) {
            message += `*Room Details*\n`;
            roomDetails.forEach((room, index) => {
                const currencySymbol = getCurrencySymbol(room.currency);
                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                
                message += `${room.roomType} | ${mealPlanLabel}\n`;
                message += `Rate: ${currencySymbol}${room.price}\n`;
                if (room.extraBed && room.extraBedPrice) {
                    message += `Extra Bed: ${currencySymbol}${room.extraBedPrice}\n`;
                }
                if (room.childWithoutBed && room.childWithoutBedPrice) {
                    message += `Child without bed: ${currencySymbol}${room.childWithoutBedPrice}\n`;
                }
                message += `\n`;
            });
            
            // Add global validity dates once at the end
            if (globalValidFrom && globalValidTo) {
                const formattedFrom = formatDateForDisplay(globalValidFrom);
                const formattedTo = formatDateForDisplay(globalValidTo);
                message += `Valid for all rooms: ${formattedFrom} to ${formattedTo}\n\n`;
            }
        }
        
        message += `📍 *Location*\n`;
        if (address) message += `${address}`;
        if (city) message += `, ${city}`;
        if (state) message += `, ${state}`;
        if (country) message += `, ${country}`;
        message += `\n\n`;

        if (latitude && longitude) {
            message += `https://www.google.com/maps?q=${latitude},${longitude}\n\n`;
        }
        
        message += `📞 *Contact*\n`;
        if (contactName) message += `${contactName}\n`;
        if (phone) message += `${phone}\n`;
        if (email) message += `${email}\n\n`;
        message += `Please share your enquiries for the best available rates.\n`;
        message += `We look forward to working with you.\n\n`;
        message += `Best regards.`;
        
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, "_blank");
    };

    // Share via Email
    const shareViaEmail = () => {
        if (!selectedHotel) return;

        const hotelName = selectedHotel.hotelName || "Hotel";
        const stars = selectedHotel.starRating || selectedHotel.starCategory || "";
        const accommodationType = selectedHotel.accommodationType || selectedHotel.accommdationType || "Hotel";
        
        const address = selectedHotel.address || "";
        const city = selectedHotel.city || "";
        const state = selectedHotel.state || "";
        const country = selectedHotel.country || "";
        
        const contactName = selectedHotel.contactInfo?.contactName || user?.name || "";
        const phone = selectedHotel.contactInfo?.phone || user?.phoneNumber || "";
        const whatsapp = selectedHotel.contactInfo?.whatsApp || user?.whatsAppNumber || "";
        const email = selectedHotel.contactInfo?.email || user?.email || "";
        
        const latitude = selectedHotel.latitude || "";
        const longitude = selectedHotel.longitude || "";

        const subject = `✨ Attractive Offers - ${hotelName} ${stars ? stars + '⭐' : ''}`;

        let body = `<html><body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">`;
        
        // Header with hotel info
        body += `<div style="text-align: center; margin-bottom: 30px; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 10px; color: white;">`;
        body += `<h1 style="margin: 0 0 10px 0; font-size: 28px;">${hotelName}</h1>`;
        if (stars) {
            body += `<div style="font-size: 20px; margin-bottom: 10px;">`;
            for (let i = 1; i <= 5; i++) {
                if (i <= stars) {
                    body += `★`;
                } else {
                    body += `☆`;
                }
            }
            body += ` ${accommodationType}</div>`;
        }
        body += `<p style="margin: 0; font-size: 18px; opacity: 0.9;">Exclusive Hotel Offer</p>`;
        body += `</div>`;
        
        // Introduction
        body += `<div style="text-align: center; margin-bottom: 30px;">`;
        body += `<h2 style="color: #2c3e50; margin-bottom: 15px;">✨ Attractive Offers Available ✨</h2>`;
        body += `<p style="font-size: 16px; color: #555;">Hope you are doing well. We are pleased to present you with exclusive rates for this property.</p>`;
        body += `</div>`;
        
        // Room Details Section
        if (roomDetails.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #3498db; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #3498db; color: white; padding: 15px; font-size: 18px; font-weight: bold;">🏨 Room Details & Rates</div>`;
            body += `<div style="padding: 20px;">`;
            
            roomDetails.forEach((room, index) => {
                const currencySymbol = getCurrencySymbol(room.currency);
                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                
                body += `<div style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #3498db;">`;
                body += `<h3 style="color: #2c3e50; margin: 0 0 10px 0;">${room.roomType}</h3>`;
                body += `<p style="margin: 5px 0; color: #555;"><strong>Meal Plan:</strong> ${mealPlanLabel}</p>`;
                body += `<p style="margin: 5px 0; color: #555;"><strong>Rate:</strong> <span style="color: #e74c3c; font-weight: bold; font-size: 18px;">${currencySymbol}${room.price}</span> per room per night</p>`;
                
                if (room.extraBed && room.extraBedPrice) {
                    body += `<p style="margin: 5px 0; color: #27ae60;"><strong>Extra Bed:</strong> ${currencySymbol}${room.extraBedPrice}</p>`;
                }
                if (room.childWithoutBed && room.childWithoutBedPrice) {
                    body += `<p style="margin: 5px 0; color: #e67e22;"><strong>Child without bed:</strong> ${currencySymbol}${room.childWithoutBedPrice}</p>`;
                }
                body += `</div>`;
            });
            
            // Add global validity dates once
            if (globalValidFrom && globalValidTo) {
                const formattedFrom = formatDateForDisplay(globalValidFrom);
                const formattedTo = formatDateForDisplay(globalValidTo);
                body += `<p style="margin: 15px 0 5px 0; color: #7f8c8d; font-size: 14px;"><strong>Valid for all rooms:</strong> ${formattedFrom} to ${formattedTo}</p>`;
            }
            
            body += `</div></div>`;
        }
        
        // Location Section
        body += `<div style="margin-bottom: 30px; border: 2px solid #2ecc71; border-radius: 10px; overflow: hidden;">`;
        body += `<div style="background: #2ecc71; color: white; padding: 15px; font-size: 18px; font-weight: bold;">📍 Location</div>`;
        body += `<div style="padding: 20px;">`;
        body += `<p style="margin: 10px 0;"><strong>Address:</strong> ${address || 'Not Available'}</p>`;
        if (city || state || country) {
            body += `<p style="margin: 10px 0;"><strong>Location:</strong> ${[city, state, country].filter(Boolean).join(', ')}</p>`;
        }
        if (latitude && longitude) {
            body += `<p style="margin: 10px 0;"><a href="https://www.google.com/maps?q=${latitude},${longitude}" target="_blank" style="color: #3498db; text-decoration: none;">📍 View on Google Maps</a></p>`;
        }
        body += `</div></div>`;
        
        // Contact Section
        body += `<div style="margin-bottom: 30px; border: 2px solid #9b59b6; border-radius: 10px; overflow: hidden;">`;
        body += `<div style="background: #9b59b6; color: white; padding: 15px; font-size: 18px; font-weight: bold;">📞 Contact Information</div>`;
        body += `<div style="padding: 20px;">`;
        if (contactName) body += `<p style="margin: 10px 0;"><strong>Contact Person:</strong> ${contactName}</p>`;
        if (phone) body += `<p style="margin: 10px 0;"><strong>Phone:</strong> ${phone}</p>`;
        if (whatsapp) body += `<p style="margin: 10px 0;"><strong>WhatsApp:</strong> ${whatsapp}</p>`;
        if (email) body += `<p style="margin: 10px 0;"><strong>Email:</strong> <a href="mailto:${email}" style="color: #3498db;">${email}</a></p>`;
        body += `</div></div>`;
        
        // Additional Information Sections
        if (amenities.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #f39c12; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #f39c12; color: white; padding: 15px; font-size: 18px; font-weight: bold;">⭐ Hotel Amenities</div>`;
            body += `<div style="padding: 20px;">`;
            body += `<ul style="margin: 0; padding-left: 20px;">`;
            amenities.forEach((amenity, index) => {
                body += `<li style="margin: 8px 0; color: #555;">${amenity.point}</li>`;
            });
            body += `</ul>`;
            body += `</div></div>`;
        }
        
        // Cancellation Policy
        if (cancellationPolicy.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #e74c3c; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #e74c3c; color: white; padding: 15px; font-size: 18px; font-weight: bold;">⚠️ Cancellation Policy</div>`;
            body += `<div style="padding: 20px;">`;
            body += `<ul style="margin: 0; padding-left: 20px;">`;
            cancellationPolicy.forEach((point, index) => {
                body += `<li style="margin: 8px 0; color: #555;">${point.point}</li>`;
            });
            body += `</ul>`;
            body += `</div></div>`;
        }
        
        // Child Policy
        if (childPolicy.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #e67e22; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #e67e22; color: white; padding: 15px; font-size: 18px; font-weight: bold;">👶 Child Policy</div>`;
            body += `<div style="padding: 20px;">`;
            body += `<ul style="margin: 0; padding-left: 20px;">`;
            childPolicy.forEach((point, index) => {
                body += `<li style="margin: 8px 0; color: #555;">${point.point}</li>`;
            });
            body += `</ul>`;
            body += `</div></div>`;
        }
        
        // Bank Details
        if (bankDetails.length > 0) {
            body += `<div style="margin-bottom: 30px; border: 2px solid #34495e; border-radius: 10px; overflow: hidden;">`;
            body += `<div style="background: #34495e; color: white; padding: 15px; font-size: 18px; font-weight: bold;">🏦 Bank Details</div>`;
            body += `<div style="padding: 20px;">`;
            bankDetails.forEach((bank, index) => {
                body += `<div style="margin-bottom: 15px; padding: 10px; background: #f8f9fa; border-radius: 5px;">`;
                body += `<p style="margin: 5px 0;"><strong>Bank:</strong> ${bank.bankName}</p>`;
                body += `<p style="margin: 5px 0;"><strong>Account Number:</strong> ${bank.accountNumber}</p>`;
                if (bank.swiftIfscCode) {
                    body += `<p style="margin: 5px 0;"><strong>SWIFT/IFSC:</strong> ${bank.swiftIfscCode}</p>`;
                }
                body += `</div>`;
            });
            body += `</div></div>`;
        }
        
        // Call to Action
        body += `<div style="text-align: center; margin-top: 40px; padding: 30px; background: #ecf0f1; border-radius: 10px;">`;
        body += `<h3 style="color: #2c3e50; margin-bottom: 20px;">Ready to Book?</h3>`;
        body += `<p style="font-size: 16px; color: #555; margin-bottom: 25px;">Please share your enquiries for the best available rates and exclusive offers.</p>`;
        body += `<p style="font-size: 16px; color: #555;">We look forward to working with you and providing exceptional service.</p>`;
        body += `</div>`;
        
        // Footer
        body += `<div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #bdc3c7; color: #7f8c8d; font-size: 14px;">`;
        body += `<p style="margin: 5px 0;">Best regards,</p>`;
        body += `<p style="margin: 5px 0; font-weight: bold;">${user?.companyname || 'Hotel Management Team'}</p>`;
        body += `<p style="margin: 5px 0;">For inquiries: ${email || phone || 'Contact us'}</p>`;
        body += `</div>`;
        
        body += `</body></html>`;

        const encodedSubject = encodeURIComponent(subject);
        const encodedBody = encodeURIComponent(body);

        window.open(
            `https://mail.google.com/mail/?view=cm&fs=1&su=${encodedSubject}&body=${encodedBody}`,
            "_blank"
        );
    };

    // Preview Function - Modal
    const handlePreview = () => {
        setShowPreviewModal(true);
        setIsDescriptionExpanded(false);
    };

    // Close Preview Modal
    const closePreviewModal = () => {
        setShowPreviewModal(false);
        setIsDescriptionExpanded(false);
    };

    // Toggle description expand
    const toggleDescriptionExpand = () => {
        setIsDescriptionExpanded(!isDescriptionExpanded);
    };

    // Room Details Functions
    const handleAddRoom = () => {
        if (newRoom.roomType && newRoom.mealPlan && newRoom.price) {
            // Validate extra bed and child without bed prices
            if (newRoom.extraBed && !newRoom.extraBedPrice) {
                alert("Please enter Extra Bed price");
                return;
            }
            if (newRoom.childWithoutBed && !newRoom.childWithoutBedPrice) {
                alert("Please enter Child without bed price");
                return;
            }
            
            setRoomDetails([...roomDetails, { 
                ...newRoom, 
                id: Date.now()
            }]);
            setNewRoom({
                roomType: "",
                mealPlan: "CP",
                extraBed: false,
                extraBedPrice: "",
                childWithoutBed: false,
                childWithoutBedPrice: "",
                price: "",
                currency: "INR"
            });
            setShowAddRoomForm(false);
            setRoomTypeSearchTerm("");
        }
    };

    const handleDeleteRoom = (roomId) => {
        setRoomDetails(roomDetails.filter(room => room.id !== roomId));
    };

    const handleEditRoom = (room) => {
        setNewRoom({
            roomType: room.roomType,
            mealPlan: room.mealPlan,
            extraBed: room.extraBed || false,
            extraBedPrice: room.extraBedPrice || "",
            childWithoutBed: room.childWithoutBed || false,
            childWithoutBedPrice: room.childWithoutBedPrice || "",
            price: room.price,
            currency: room.currency
        });
        setRoomTypeSearchTerm(room.roomType);
        setShowAddRoomForm(true);
        handleDeleteRoom(room.id);
    };

    const handleNewRoomChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewRoom(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Global validity date handlers
    const handleGlobalValidFromChange = (e) => {
        setGlobalValidFrom(e.target.value);
    };

    const handleGlobalValidToChange = (e) => {
        setGlobalValidTo(e.target.value);
    };

    // Amenities Functions
    const handleAddAmenityPoint = () => {
        if (newAmenityPoint.trim()) {
            setAmenities([...amenities, { 
                id: Date.now(), 
                point: newAmenityPoint.trim() 
            }]);
            setNewAmenityPoint("");
        }
    };

    const handleDeleteAmenityPoint = (pointId) => {
        setAmenities(amenities.filter(point => point.id !== pointId));
    };

    const handleEditAmenityPoint = (point) => {
        setNewAmenityPoint(point.point);
        handleDeleteAmenityPoint(point.id);
    };

    // Terms & Conditions Functions
    const handleAddTermPoint = () => {
        if (newTermPoint.trim()) {
            setTermsAndConditions([...termsAndConditions, { 
                id: Date.now(), 
                point: newTermPoint.trim() 
            }]);
            setNewTermPoint("");
        }
    };

    const handleDeleteTermPoint = (pointId) => {
        setTermsAndConditions(termsAndConditions.filter(point => point.id !== pointId));
    };

    const handleEditTermPoint = (point) => {
        setNewTermPoint(point.point);
        handleDeleteTermPoint(point.id);
    };

    // Cancellation Policy Functions
    const handleAddCancellationPoint = () => {
        if (newCancellationPoint.trim()) {
            setCancellationPolicy([...cancellationPolicy, { 
                id: Date.now(), 
                point: newCancellationPoint.trim() 
            }]);
            setNewCancellationPoint("");
        }
    };

    const handleDeleteCancellationPoint = (pointId) => {
        setCancellationPolicy(cancellationPolicy.filter(point => point.id !== pointId));
    };

    const handleEditCancellationPoint = (point) => {
        setNewCancellationPoint(point.point);
        handleDeleteCancellationPoint(point.id);
    };

    // Child Policy Functions
    const handleAddChildPolicyPoint = () => {
        if (newChildPolicyPoint.trim()) {
            setChildPolicy([...childPolicy, { 
                id: Date.now(), 
                point: newChildPolicyPoint.trim() 
            }]);
            setNewChildPolicyPoint("");
        }
    };

    const handleDeleteChildPolicyPoint = (pointId) => {
        setChildPolicy(childPolicy.filter(point => point.id !== pointId));
    };

    const handleEditChildPolicyPoint = (point) => {
        setNewChildPolicyPoint(point.point);
        handleDeleteChildPolicyPoint(point.id);
    };

    // Bank Details Functions
    const handleAddBankDetail = () => {
        if (newBankDetail.bankName && newBankDetail.accountNumber) {
            setBankDetails([...bankDetails, { ...newBankDetail, id: Date.now() }]);
            setNewBankDetail({
                bankName: "",
                accountNumber: "",
                swiftIfscCode: ""
            });
            setShowAddBankForm(false);
        }
    };

    const handleDeleteBankDetail = (bankId) => {
        setBankDetails(bankDetails.filter(bank => bank.id !== bankId));
    };

    const handleEditBankDetail = (bank) => {
        setNewBankDetail(bank);
        setShowAddBankForm(true);
        handleDeleteBankDetail(bank.id);
    };

    const handleBankDetailChange = (e) => {
        const { name, value } = e.target;
        setNewBankDetail(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Contact Details Functions
    const handleAddContact = () => {
        if (newContact.contactName || newContact.email || newContact.phone || newContact.whatsApp) {
            setSelectedHotel(prev => ({
                ...prev,
                contactInfo: {
                    contactName: newContact.contactName || prev.contactInfo?.contactName,
                    email: newContact.email || prev.contactInfo?.email,
                    phone: newContact.phone || prev.contactInfo?.phone,
                    whatsApp: newContact.whatsApp || prev.contactInfo?.whatsApp
                }
            }));
            setNewContact({
                contactName: "",
                email: "",
                phone: "",
                whatsApp: ""
            });
            setShowAddContactForm(false);
        }
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setNewContact(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Get currency symbol
    const getCurrencySymbol = (currencyCode) => {
        const currency = currencyOptions.find(c => c.code === currencyCode);
        return currency ? currency.symbol : currencyCode;
    };

    // Format date for display (DD MMM YYYY)
    const formatDate = (dateString) => {
        if (!dateString || dateString === "Not specified" || dateString === "") return "Not specified";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return "Not specified";
        }
    };
    
    // Format date for display in messages
    const formatDateForDisplay = (dateString) => {
        if (!dateString || dateString === "") return "Not specified";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return "Not specified";
        }
    };

    // Render star rating
    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                stars.push(<i key={i} className="bi bi-star-fill text-warning"></i>);
            } else if (i === fullStars + 1 && hasHalfStar) {
                stars.push(<i key={i} className="bi bi-star-half text-warning"></i>);
            } else {
                stars.push(<i key={i} className="bi bi-star text-warning"></i>);
            }
        }
        return <span className="d-inline-flex align-items-center">{stars}</span>;
    };

    // Tab navigation
    const tabs = [
        { id: "roomDetails", label: "Room Details", icon: "bi-bed" },
        { id: "amenities", label: "Amenities", icon: "bi-wifi" },
        { id: "terms", label: "Terms & Conditions", icon: "bi-file-text" },
        { id: "cancellation", label: "Cancellation Policy", icon: "bi-calendar-event" },
        { id: "childPolicy", label: "Child Policy", icon: "bi-person" },
        { id: "bankDetails", label: "Bank Details", icon: "bi-bank" }
    ];

    // Render room type field with auto-complete
    const renderRoomTypeField = () => (
        <div className="col-md-3">
            <label className="form-label">Room Type *</label>
            <div className="position-relative" ref={roomTypeInputRef}>
                <input
                    type="text"
                    className="form-control"
                    value={roomTypeSearchTerm || newRoom.roomType}
                    onChange={(e) => {
                        handleRoomTypeSearch(e.target.value);
                        setNewRoom(prev => ({ ...prev, roomType: e.target.value }));
                    }}
                    placeholder="Search room type..."
                    required
                    onFocus={() => {
                        if (roomTypeSearchTerm.length > 0 || newRoom.roomType.length > 0) {
                            handleRoomTypeSearch(roomTypeSearchTerm || newRoom.roomType);
                        }
                    }}
                />
                {showRoomTypeDropdown && roomTypeSuggestions.length > 0 && (
                    <div className="position-absolute w-100 bg-white border rounded shadow-lg room-type-dropdown"
                         style={{ 
                             zIndex: 1050, 
                             maxHeight: '200px', 
                             overflowY: 'auto',
                             top: '100%',
                             left: 0,
                             marginTop: '2px'
                         }}>
                        {roomTypeSuggestions.map((type, index) => (
                            <div key={index}
                                 className="dropdown-item cursor-pointer"
                                 onClick={() => selectRoomType(type)}
                                 style={{ 
                                     padding: '8px 12px', 
                                     cursor: 'pointer',
                                     borderBottom: '1px solid #f0f0f0'
                                 }}>
                                {type}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Preview Modal Component - FIXED: Added date binding and description expand
    const PreviewModal = () => {
        if (!showPreviewModal || !selectedHotel) return null;

        const description = selectedHotel.overview || selectedHotel.description || 'No description available.';
        const isLongDescription = description.length > 300;
        const displayedDescription = isDescriptionExpanded ? description : description.slice(0, 300) + (description.length > 300 ? '...' : '');

        return (
            <div className="modal-overlay" style={styles.modalOverlay}>
                <div className="modal-container" style={styles.modalContainer}>
                    {/* Modal Header */}
                    <div className="modal-header" style={styles.modalHeader}>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-eye me-2" style={{ fontSize: '1.5rem' }}></i>
                            <h5 className="mb-0" style={{ color: '#fff' }}>
                                Preview - {selectedHotel.hotelName}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={closePreviewModal}
                            aria-label="Close"
                        ></button>
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body" style={styles.modalBody}>
                        {/* Hotel Header Section */}
                        <div className="hotel-header" style={styles.hotelHeader}>
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    {selectedHotel.hotelImage ? (
                                        <img
                                            src={selectedHotel.hotelImage}
                                            alt={selectedHotel.hotelName}
                                            style={styles.hotelImage}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://via.placeholder.com/400x250/667eea/ffffff?text=${encodeURIComponent(selectedHotel.hotelName?.substring(0, 30) || 'Hotel')}`;
                                            }}
                                        />
                                    ) : (
                                        <div style={styles.hotelImagePlaceholder}>
                                            <i className="bi bi-building" style={{ fontSize: '48px' }}></i>
                                            <div className="mt-2">Hotel Image</div>
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-8">
                                    <h2 style={styles.hotelName}>{selectedHotel.hotelName}</h2>
                                    <div className="d-flex align-items-center mb-3">
                                        {renderStars(selectedHotel.starRating || selectedHotel.starCategory || 0)}
                                        <span className="ms-2 fw-bold" style={{ color: '#ffc107' }}>
                                            {selectedHotel.starRating || selectedHotel.starCategory || 'N/A'} Star
                                        </span>
                                        <span className="ms-3 badge bg-primary">
                                            {selectedHotel.accommodationType || selectedHotel.accommdationType || 'Hotel'}
                                        </span>
                                    </div>
                                    
                                    {/* Description with View More/Less */}
                                    <div style={styles.hotelDescription}>
                                        <p className="mb-2" style={{ 
                                            lineHeight: '1.6', 
                                            marginBottom: '10px',
                                            whiteSpace: 'pre-line'
                                        }}>
                                            {displayedDescription}
                                        </p>
                                        {isLongDescription && (
                                            <button
                                                onClick={toggleDescriptionExpand}
                                                className="btn btn-link p-0"
                                                style={{ 
                                                    color: '#667eea', 
                                                    textDecoration: 'none',
                                                    fontWeight: '600',
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                {isDescriptionExpanded ? (
                                                    <><i className="bi bi-chevron-up me-1"></i> View Less</>
                                                ) : (
                                                    <><i className="bi bi-chevron-down me-1"></i> View Full Description</>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact & Property Details Section */}
                        <div className="row mb-4">
                            {/* Contact Details */}
                            <div className="col-md-6 mb-4">
                                <div className="card h-100" style={styles.detailCard}>
                                    <div className="card-header" style={styles.detailCardHeader}>
                                        <i className="bi bi-person me-2"></i>
                                        Contact Details
                                    </div>
                                    <div className="card-body">
                                        {(selectedHotel.contactInfo?.contactName || selectedHotel.contactName) && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Contact Person</small>
                                                <strong>{selectedHotel.contactInfo?.contactName || selectedHotel.contactName}</strong>
                                            </div>
                                        )}
                                        {(selectedHotel.contactInfo?.email || selectedHotel.email) && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Email</small>
                                                <strong>
                                                    <a href={`mailto:${selectedHotel.contactInfo?.email || selectedHotel.email}`} style={{ color: '#0d6efd' }}>
                                                        {selectedHotel.contactInfo?.email || selectedHotel.email}
                                                    </a>
                                                </strong>
                                            </div>
                                        )}
                                        {(selectedHotel.contactInfo?.phone || selectedHotel.phone) && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Phone</small>
                                                <strong>{selectedHotel.contactInfo?.phone || selectedHotel.phone}</strong>
                                            </div>
                                        )}
                                        {(selectedHotel.contactInfo?.whatsApp || selectedHotel.whatsapp) && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">WhatsApp</small>
                                                <strong>{selectedHotel.contactInfo?.whatsApp || selectedHotel.whatsapp}</strong>
                                            </div>
                                        )}
                                        {!selectedHotel.contactInfo?.contactName && !selectedHotel.contactInfo?.email && 
                                         !selectedHotel.contactInfo?.phone && !selectedHotel.contactInfo?.whatsApp && 
                                         !selectedHotel.contactName && !selectedHotel.email && !selectedHotel.phone && !selectedHotel.whatsapp && (
                                            <div className="text-muted text-center py-3">
                                                <i className="bi bi-info-circle me-1"></i>
                                                No contact details available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Property Details */}
                            <div className="col-md-6 mb-4">
                                <div className="card h-100" style={styles.detailCard}>
                                    <div className="card-header" style={styles.detailCardHeader}>
                                        <i className="bi bi-geo-alt me-2"></i>
                                        Property Details
                                    </div>
                                    <div className="card-body">
                                        {selectedHotel.address && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block">Address</small>
                                                <strong>{selectedHotel.address}</strong>
                                            </div>
                                        )}
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <small className="text-muted d-block">City</small>
                                                <strong>{selectedHotel.city || 'N/A'}</strong>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block">Country</small>
                                                <strong>{selectedHotel.country || 'N/A'}</strong>
                                            </div>
                                        </div>
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <small className="text-muted d-block">Property Type</small>
                                                <strong>{selectedHotel.accommodationType || selectedHotel.accommdationType || 'N/A'}</strong>
                                            </div>
                                        </div>
                                        {selectedHotel.latitude && selectedHotel.longitude && (
                                            <div className="mb-3">
                                                <a
                                                    href={`https://www.google.com/maps?q=${selectedHotel.latitude},${selectedHotel.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="bi bi-geo-alt me-1"></i>
                                                    View on Google Maps
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Room Details Section - FIXED: Show global validity dates from state */}
                        {roomDetails.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-bed me-2"></i>
                                    Room Details
                                </h4>
                                
                                {/* Global Validity Dates Display - FIXED: Using globalValidFrom and globalValidTo */}
                                {globalValidFrom && globalValidTo && (
                                    <div className="alert alert-info mb-3">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        <strong>Valid for all rooms:</strong> {formatDate(globalValidFrom)} to {formatDate(globalValidTo)}
                                    </div>
                                )}
                                
                                <div className="table-responsive">
                                    <table className="table table-hover" style={{ fontSize: '0.9rem' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Room Type</th>
                                                <th>Meal Plan</th>
                                                <th>Price</th>
                                                <th>Extra Bed</th>
                                                <th>Child w/o Bed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {roomDetails.map((room) => {
                                                const currencySymbol = getCurrencySymbol(room.currency);
                                                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                                                
                                                return (
                                                    <tr key={room.id}>
                                                        <td>
                                                            <strong>{room.roomType}</strong>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info">
                                                                {mealPlanLabel}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                                                                {currencySymbol}{room.price}
                                                            </span>
                                                            <small className="text-muted ms-1">per night</small>
                                                        </td>
                                                        <td>
                                                            {room.extraBed && room.extraBedPrice ? (
                                                                <span className="badge bg-success">
                                                                    {currencySymbol}{room.extraBedPrice}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {room.childWithoutBed && room.childWithoutBedPrice ? (
                                                                <span className="badge bg-info">
                                                                    {currencySymbol}{room.childWithoutBedPrice}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Additional Sections */}
                        {amenities.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-wifi me-2"></i>
                                    Amenities
                                </h4>
                                <div className="row">
                                    {amenities.map((amenity, index) => (
                                        <div key={amenity.id} className="col-md-4 mb-2">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                <span>{amenity.point}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Policies Section */}
                        {(termsAndConditions.length > 0 || cancellationPolicy.length > 0 || childPolicy.length > 0) && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Policies
                                </h4>
                                
                                {termsAndConditions.length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="text-primary mb-3">
                                            <i className="bi bi-file-text me-2"></i>
                                            Terms & Conditions
                                        </h5>
                                        <div className="row">
                                            {termsAndConditions.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-primary"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {cancellationPolicy.length > 0 && (
                                    <div className="mb-4">
                                        <h5 className="text-danger mb-3">
                                            <i className="bi bi-calendar-x me-2"></i>
                                            Cancellation Policy
                                        </h5>
                                        <div className="row">
                                            {cancellationPolicy.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-danger"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {childPolicy.length > 0 && (
                                    <div>
                                        <h5 className="text-success mb-3">
                                            <i className="bi bi-person-plus me-2"></i>
                                            Child Policy
                                        </h5>
                                        <div className="row">
                                            {childPolicy.map((point) => (
                                                <div key={point.id} className="col-12 mb-2">
                                                    <div className="d-flex">
                                                        <i className="bi bi-dot me-2 text-success"></i>
                                                        <span>{point.point}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Bank Details Section */}
                        {bankDetails.length > 0 && (
                            <div className="mb-4">
                                <h4 style={styles.sectionTitle}>
                                    <i className="bi bi-bank me-2"></i>
                                    Bank Details
                                </h4>
                                <div className="row">
                                    {bankDetails.map((bank) => (
                                        <div key={bank.id} className="col-md-6 mb-3">
                                            <div className="card" style={styles.bankCard}>
                                                <div className="card-body">
                                                    <h6 className="text-primary mb-3">{bank.bankName}</h6>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Account Number</small>
                                                        <strong>{bank.accountNumber}</strong>
                                                    </div>
                                                    {bank.swiftIfscCode && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">SWIFT/IFSC Code</small>
                                                            <strong>{bank.swiftIfscCode}</strong>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="modal-footer" style={styles.modalFooter}>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={shareViaWhatsApp}
                        >
                            <i className="bi bi-whatsapp me-1"></i>
                            Share via WhatsApp
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={shareViaEmail}
                        >
                            <i className="bi bi-envelope me-1"></i>
                            Share via Email
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={closePreviewModal}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // CSS Styles for the Modal
    const styles = {
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
        },
        modalContainer: {
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        },
        modalHeader: {
            backgroundColor: '#667eea',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        modalBody: {
            padding: '30px',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 140px)',
        },
        modalFooter: {
            padding: '20px',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
        },
        hotelHeader: {
            backgroundColor: '#f8f9fa',
            padding: '30px',
            borderRadius: '10px',
            marginBottom: '30px',
            border: '1px solid #e9ecef',
        },
        hotelImage: {
            width: '100%',
            height: '250px',
            objectFit: 'cover',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        hotelImagePlaceholder: {
            width: '100%',
            height: '250px',
            backgroundColor: '#667eea',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            flexDirection: 'column',
        },
        hotelName: {
            color: '#2c3e50',
            marginBottom: '15px',
            fontWeight: '700',
        },
        hotelDescription: {
            color: '#6c757d',
            lineHeight: '1.6',
            fontSize: '1rem',
        },
        detailCard: {
            border: '1px solid #e9ecef',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease',
            height: '100%',
        },
        detailCardHeader: {
            backgroundColor: '#f8f9fa',
            color: '#495057',
            fontWeight: '600',
            borderBottom: '1px solid #e9ecef',
            padding: '15px 20px',
        },
        sectionTitle: {
            color: '#2c3e50',
            borderBottom: '2px solid #667eea',
            paddingBottom: '10px',
            marginBottom: '20px',
            fontWeight: '600',
        },
        bankCard: {
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
        }
    };

    // Pagination calculations
    const indexOfLastResult = currentPage * resultsPerPage;
    const indexOfFirstResult = indexOfLastResult - resultsPerPage;
    const currentResults = savedHotels.slice(indexOfFirstResult, indexOfLastResult);
    const totalPages = Math.ceil(savedHotels.length / resultsPerPage);

    if (loading) return <p>Loading...</p>;

    if (!user) {
        navigate("/");
    }

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                {/* <HotelDmcTabs pageName="DMCPROPERTIES" /> */}
                            </div>
                            <div className="card-body">
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title text-primary mb-0">
                                                    <i className="bi bi-building-check me-2"></i>
                                                    My Properties ({savedHotels.length})
                                                </h5>
                                                <p className="text-muted mb-0">
                                                    Manage your saved hotels and add custom details
                                                </p>
                                            </div>
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={fetchSavedHotels}
                                                disabled={isLoading}
                                            >
                                                <i className="bi bi-arrow-clockwise me-2"></i>
                                                {isLoading ? "Refreshing..." : "Refresh"}
                                            </button>
                                        </div>

                                        {/* Search Bar */}
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <form onSubmit={handleSearchSubmit}>
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-light">
                                                            <i className="bi bi-search"></i>
                                                        </span>
                                                        <input
                                                            ref={searchInputRef}
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search hotels by name (minimum 3 characters)..."
                                                            value={searchTerm}
                                                            onChange={handleSearchChange}
                                                            aria-label="Search hotels"
                                                        />
                                                        <button
                                                            className="btn btn-primary"
                                                            type="submit"
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? "Searching..." : "Search"}
                                                        </button>
                                                        {searchTerm && (
                                                            <button
                                                                className="btn btn-outline-danger"
                                                                type="button"
                                                                onClick={clearSearch}
                                                                disabled={isLoading}
                                                            >
                                                                <i className="bi bi-x"></i>
                                                                Clear
                                                            </button>
                                                        )}
                                                    </div>
                                                    {searchTerm.length > 0 && searchTerm.length < 3 && (
                                                        <small className="text-warning mt-1 d-block">
                                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                                            Please enter at least 3 characters to search
                                                        </small>
                                                    )}
                                                    {isSearching && (
                                                        <small className="text-info mt-1 d-block">
                                                            <i className="bi bi-info-circle me-1"></i>
                                                            Searching for: "{searchTerm}"
                                                        </small>
                                                    )}
                                                </form>
                                            </div>
                                        </div>

                                        {/* Loading State */}
                                        {isLoading && (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mt-3">
                                                    {isSearching ? "Searching properties..." : "Loading your properties..."}
                                                </p>
                                            </div>
                                        )}

                                        {/* Error State */}
                                        {error && !isLoading && (
                                            <div className="alert alert-danger text-center py-5">
                                                <i className="bi bi-exclamation-triangle mb-3 text-danger" style={{ fontSize: '48px' }}></i>
                                                <h5>Error Loading Properties</h5>
                                                <p className="text-muted">{error}</p>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary"
                                                    onClick={fetchSavedHotels}
                                                >
                                                    Try Again
                                                </button>
                                            </div>
                                        )}

                                        {/* No Properties State */}
                                        {!isLoading && !error && savedHotels.length === 0 && (
                                            <div className="text-center py-5">
                                                <div className="display-1 text-muted mb-3">
                                                    <i className="bi bi-building-x" style={{ fontSize: '64px' }}></i>
                                                </div>
                                                <h4 className="mb-3">
                                                    {isSearching ? "No Properties Found" : "No Properties Saved Yet"}
                                                </h4>
                                                <p className="text-muted mb-4">
                                                    {isSearching 
                                                        ? `No properties found matching "${searchTerm}". Try a different search term.`
                                                        : "You haven't saved any hotels to your DMC properties list."}
                                                </p>
                                                {!isSearching && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={() => navigate("/dmc-hotels")}
                                                    >
                                                        <i className="bi bi-search me-2"></i>
                                                        Search and Add Hotels
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Properties List */}
                                        {!isLoading && !error && savedHotels.length > 0 && (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                                                    <div>
                                                        <h5 className="mb-0">
                                                            {savedHotels.length} Properties
                                                            {isSearching && (
                                                                <span className="text-primary ms-2">
                                                                    (Search results for "{searchTerm}")
                                                                </span>
                                                            )}
                                                        </h5>
                                                        <small className="text-muted">
                                                            Showing {currentResults.length} of {savedHotels.length} properties
                                                        </small>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">
                                                            Page {currentPage} of {totalPages}
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4 mb-4">
                                                    {currentResults.map((hotel, index) => (
                                                        <div key={hotel.companyHotelId || index} className="col">
                                                            <div 
                                                                className={`card h-100 hotel-card ${selectedHotel?.companyHotelId === hotel.companyHotelId ? 'border-primary border-2' : 'border-light'}`}
                                                                onClick={() => handleHotelSelect(hotel)}
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                                }}
                                                            >
                                                                <div className="position-relative">
                                                                    {hotel.hotelImage ? (
                                                                        <img
                                                                            src={hotel.hotelImage}
                                                                            className="card-img-top"
                                                                            alt={hotel.hotelName}
                                                                            style={{ height: '180px', objectFit: 'cover', width: '100%' }}
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src = `https://via.placeholder.com/300x180/cccccc/666666?text=${encodeURIComponent(hotel.hotelName?.substring(0, 20) || 'Hotel')}`;
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <div className="card-img-top bg-gradient-primary d-flex align-items-center justify-content-center"
                                                                             style={{ height: '180px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                                                            <i className="bi bi-building text-white" style={{ fontSize: '48px' }}></i>
                                                                        </div>
                                                                    )}
                                                                    {hotel.isOwner && (
                                                                        <span className="position-absolute top-0 start-0 m-2 badge bg-success">
                                                                            <i className="bi bi-check-circle me-1"></i>Owner
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="card-body">
                                                                    {/* Hotel Name with Star Rating */}
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <h6 className="card-title text-truncate mb-0 me-2">
                                                                            {hotel.hotelName}
                                                                        </h6>
                                                                        <div className="d-flex align-items-center flex-shrink-0">
                                                                            {renderStars(hotel.starRating || hotel.starCategory || 0)}
                                                                            <span className="ms-1 fw-bold text-warning">
                                                                                {hotel.starRating || hotel.starCategory || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Location */}
                                                                    <div className="d-flex align-items-center text-muted small mb-2">
                                                                        <i className="bi bi-geo-alt me-1 flex-shrink-0"></i>
                                                                        <span className="text-truncate">
                                                                            {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                                                                        </span>
                                                                    </div>
                                                                    
                                                                    {/* Custom Details Indicator */}
                                                                    {(hotel.roomDetails?.length > 0 || hotel.amenities?.length > 0) && (
                                                                        <div className="mb-2">
                                                                            <small className="text-success">
                                                                                <i className="bi bi-check-circle me-1"></i>
                                                                                Custom details added
                                                                                {hotel.roomDetails?.length > 0 && (
                                                                                    <span className="ms-2">
                                                                                        {hotel.roomDetails?.length} room(s)
                                                                                    </span>
                                                                                )}
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Property Type and Country Code */}
                                                                    <div className="mb-2">
                                                                        <span className="badge bg-primary bg-opacity-10 text-primary me-1">
                                                                            {hotel.accommodationType || hotel.accommdationType || 'Hotel'}
                                                                        </span>
                                                                        {hotel.countryCode && (
                                                                            <span className="badge bg-info bg-opacity-10 text-info me-1">
                                                                                <i className="bi bi-flag me-1" style={{ fontSize: '12px' }}></i>
                                                                                {hotel.countryCode}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="card-footer bg-transparent border-top pt-2 pb-2">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className="text-muted">
                                                                            Saved on: {new Date(hotel.savedAt || hotel.createdAt).toLocaleDateString()}
                                                                        </small>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleHotelSelect(hotel);
                                                                                }}
                                                                            >
                                                                                Manage
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-danger"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    removeHotel(hotel.hotelId);
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <nav aria-label="Properties pagination" className="mt-4">
                                                        <ul className="pagination justify-content-center flex-wrap">
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(1)}
                                                                    disabled={currentPage === 1}
                                                                >
                                                                    First
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                                    disabled={currentPage === 1}
                                                                >
                                                                    Previous
                                                                </button>
                                                            </li>
                                                            
                                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                                let pageNum;
                                                                if (totalPages <= 5) {
                                                                    pageNum = i + 1;
                                                                } else if (currentPage <= 3) {
                                                                    pageNum = i + 1;
                                                                } else if (currentPage >= totalPages - 2) {
                                                                    pageNum = totalPages - 4 + i;
                                                                } else {
                                                                    pageNum = currentPage - 2 + i;
                                                                }
                                                                
                                                                return (
                                                                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                                        <button
                                                                            className="page-link"
                                                                            onClick={() => setCurrentPage(pageNum)}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            })}
                                                            
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                                    disabled={currentPage === totalPages}
                                                                >
                                                                    Next
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(totalPages)}
                                                                    disabled={currentPage === totalPages}
                                                                >
                                                                    Last
                                                                </button>
                                                            </li>
                                                        </ul>
                                                        <div className="text-center text-muted mt-2">
                                                            Page {currentPage} of {totalPages} • Showing {currentResults.length} of {savedHotels.length} properties
                                                        </div>
                                                    </nav>
                                                )}
                                            </>
                                        )}

                                        {/* Hotel Details Section with Tabs */}
                                        {showHotelDetails && selectedHotel && (
                                            <div id="hotel-details-section" className="card border-success mt-4">
                                                <div className="card-header bg-success bg-opacity-10">
                                                    {/* Row 1: Hotel Name with Stars */}
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <i className="bi bi-building me-2 text-success"></i>
                                                            <h5 className="mb-0 text-success">
                                                                {selectedHotel.hotelName}
                                                            </h5>
                                                            <div className="ms-3 d-flex align-items-center">
                                                                {renderStars(selectedHotel.starRating || selectedHotel.starCategory || 0)}
                                                                <span className="ms-2 fw-bold text-warning">
                                                                    {selectedHotel.starRating || selectedHotel.starCategory || 'N/A'} Star
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Row 2: Action Buttons with Preview and Save */}
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-info btn-sm"
                                                            onClick={handlePreview}
                                                            title="Preview Hotel Details"
                                                        >
                                                            <i className="bi bi-eye me-1"></i>
                                                            Preview
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={saveHotelDetails}
                                                            disabled={isSaving}
                                                            title="Save Hotel Data"
                                                        >
                                                            <i className="bi bi-cloud-upload me-1"></i>
                                                            {isSaving ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={shareViaWhatsApp}
                                                            title="Share via WhatsApp Web"
                                                        >
                                                            <i className="bi bi-whatsapp me-1"></i>
                                                            WhatsApp
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm"
                                                            onClick={shareViaEmail}
                                                            title="Share via Email"
                                                        >
                                                            <i className="bi bi-envelope me-1"></i>
                                                            Email
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => removeHotel(selectedHotel.hotelId)}
                                                            title="Remove Hotel"
                                                        >
                                                            <i className="bi bi-trash me-1"></i>
                                                            Remove
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={closeHotelDetails}
                                                            title="Close"
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                                
                                                <div className="card-body">
                                                    {/* Save Status Messages */}
                                                    {saveSuccess && (
                                                        <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                                                            <i className="bi bi-check-circle-fill me-2"></i>
                                                            Hotel details saved successfully!
                                                            <button type="button" className="btn-close" onClick={() => setSaveSuccess(false)}></button>
                                                        </div>
                                                    )}
                                                    {saveError && (
                                                        <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                            {saveError}
                                                            <button type="button" className="btn-close" onClick={() => setSaveError("")}></button>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Basic Hotel Info Section */}
                                                    <div className="row mb-4">
                                                        <div className="col-md-4 mb-4 mb-md-0">
                                                            {/* Hotel Image */}
                                                            <div className="mb-3">
                                                                {selectedHotel.hotelImage ? (
                                                                    <img
                                                                        src={selectedHotel.hotelImage}
                                                                        className="img-fluid rounded shadow"
                                                                        alt={selectedHotel.hotelName}
                                                                        style={{ 
                                                                            width: '100%', 
                                                                            height: '250px',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = `https://via.placeholder.com/400x250/cccccc/666666?text=${encodeURIComponent(selectedHotel.hotelName?.substring(0, 30) || 'Hotel Image')}`;
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="bg-gradient-primary rounded shadow d-flex align-items-center justify-content-center"
                                                                        style={{ 
                                                                            height: '250px',
                                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-building text-white" style={{ fontSize: '64px' }}></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Description */}
                                                            <div className="card border-info mb-3">
                                                                <div className="card-body">
                                                                    <h6 className="card-title text-info mb-2">
                                                                        <i className="bi bi-info-circle me-1"></i>
                                                                        Description
                                                                    </h6>
                                                                    <p className="mb-0 text-muted" style={{ 
                                                                        maxHeight: '120px',
                                                                        overflowY: 'auto',
                                                                        paddingRight: '5px'
                                                                    }}>
                                                                        {selectedHotel.overview || selectedHotel.description || 'No description available for this property.'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Property Type */}
                                                            <div className="card border-primary">
                                                                <div className="card-body text-center">
                                                                    <h6 className="card-title text-muted mb-2">
                                                                        <i className="bi bi-building me-1 text-primary"></i>
                                                                        Property Type
                                                                    </h6>
                                                                    <h4 className="text-primary mb-0">
                                                                        {selectedHotel.accommodationType || selectedHotel.accommdationType || 'Hotel'}
                                                                    </h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Contact and Location Details */}
                                                        <div className="col-md-8">
                                                            <div className="row">
                                                                {/* Contact Details Card */}
                                                                <div className="col-lg-6 mb-4">
                                                                    <div className="card h-100 border">
                                                                        <div className="card-header bg-light">
                                                                            <div className="d-flex align-items-center mb-2">
                                                                                <h6 className="mb-0 text-muted">
                                                                                    <i className="bi bi-person me-2"></i>
                                                                                    Contact Details
                                                                                </h6>
                                                                            </div>
                                                                            <div className="d-flex justify-content-end">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() => setShowAddContactForm(!showAddContactForm)}
                                                                                    title="Update Contact Details"
                                                                                >
                                                                                    <i className="bi bi-pencil"></i>
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        <div className="card-body">
                                                                            <div className="mb-4">
                                                                                <div className="mb-2">
                                                                                    <strong>Contact Name:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.contactInfo?.contactName || selectedHotel.contactName || user?.name || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-2">
                                                                                    <strong>Email:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.contactInfo?.email || selectedHotel.email || user?.email || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-2">
                                                                                    <strong>Phone:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.contactInfo?.phone || selectedHotel.phone || user?.phoneNumber || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mb-3">
                                                                                    <strong>WhatsApp:</strong>
                                                                                    <span className="mb-0 ms-2 text-dark">
                                                                                        {selectedHotel.contactInfo?.whatsApp || selectedHotel.whatsapp || user?.whatsAppNumber || 'Not Available'}
                                                                                    </span>
                                                                                </div>
                                                                                
                                                                                {/* Add Contact Form */}
                                                                                {showAddContactForm && (
                                                                                    <div className="card border-info p-3 mb-3">
                                                                                        <h6 className="text-info mb-3">
                                                                                            <i className="bi bi-pencil me-1"></i>
                                                                                            Update Contact Details
                                                                                        </h6>
                                                                                        <div className="row g-2">
                                                                                            <div className="col-12">
                                                                                                <label className="form-label">Contact Name</label>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="contactName"
                                                                                                    value={newContact.contactName}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.contactInfo?.contactName || selectedHotel.contactName || user?.name || "Enter contact name"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-md-6">
                                                                                                <label className="form-label">Email</label>
                                                                                                <input
                                                                                                    type="email"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="email"
                                                                                                    value={newContact.email}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.contactInfo?.email || selectedHotel.email || user?.email || "Enter email"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-md-6">
                                                                                                <label className="form-label">Phone</label>
                                                                                                <input
                                                                                                    type="tel"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="phone"
                                                                                                    value={newContact.phone}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.contactInfo?.phone || selectedHotel.phone || user?.phoneNumber || "Enter phone number"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-12">
                                                                                                <label className="form-label">WhatsApp</label>
                                                                                                <input
                                                                                                    type="tel"
                                                                                                    className="form-control form-control-sm"
                                                                                                    name="whatsApp"
                                                                                                    value={newContact.whatsApp}
                                                                                                    onChange={handleContactChange}
                                                                                                    placeholder={selectedHotel.contactInfo?.whatsApp || selectedHotel.whatsapp || user?.whatsAppNumber || "Enter WhatsApp number"}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="col-12">
                                                                                                <div className="d-flex gap-2 mt-2">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-sm btn-primary"
                                                                                                        onClick={handleAddContact}
                                                                                                    >
                                                                                                        <i className="bi bi-check me-1"></i>
                                                                                                        Update
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-sm btn-danger"
                                                                                                        onClick={() => setShowAddContactForm(false)}
                                                                                                    >
                                                                                                        Cancel
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Property Details Card */}
                                                                <div className="col-lg-6 mb-4">
                                                                    <div className="card h-100 border">
                                                                        <div className="card-header bg-light">
                                                                            <h6 className="mb-0 text-muted">
                                                                                <i className="bi bi-info-circle me-2"></i>
                                                                                Property Details
                                                                            </h6>
                                                                        </div>

                                                                        <div className="card-body">
                                                                            {/* Location Details */}
                                                                            <div className="mb-3">
                                                                                <div className="mb-1">
                                                                                    <strong>Address:</strong>
                                                                                    <span className="ms-2 text-dark text-break">
                                                                                        {selectedHotel.address || 'Not Available'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>City:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.city || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>State:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.state || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-1">
                                                                                    <strong>Country:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.country || 'N/A'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="mb-2">
                                                                                    <strong>Postal Code:</strong>
                                                                                    <span className="ms-2 text-dark">
                                                                                        {selectedHotel.postalCode || 'N/A'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            {/* Google Maps Link */}
                                                                            {selectedHotel.latitude && selectedHotel.longitude && (
                                                                                <div className="mb-4">
                                                                                    <a
                                                                                        href={`https://www.google.com/maps?q=${selectedHotel.latitude},${selectedHotel.longitude}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="btn btn-sm btn-primary"
                                                                                    >
                                                                                        <i className="bi bi-geo-alt me-1"></i>
                                                                                        View on Google Maps
                                                                                    </a>
                                                                                </div>
                                                                            )}

                                                                            {/* Check-in / Check-out */}
                                                                            <div>
                                                                                <h6 className="text-primary mb-2">
                                                                                    <i className="bi bi-calendar me-1"></i>
                                                                                    Check-in & Check-out Times
                                                                                </h6>
                                                                                <div className="row">
                                                                                    <div className="col-md-6 mb-2">
                                                                                        <div className="card border-success">
                                                                                            <div className="card-body text-center p-2">
                                                                                                <small className="text-success">Check-in</small>
                                                                                                <h6 className="mb-0">
                                                                                                    {selectedHotel.checkIn || 'Not Specified'}
                                                                                                </h6>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-md-6 mb-2">
                                                                                        <div className="card border-info">
                                                                                            <div className="card-body text-center p-2">
                                                                                                <small className="text-info">Check-out</small>
                                                                                                <h6 className="mb-0">
                                                                                                    {selectedHotel.checkout || 'Not Specified'}
                                                                                                </h6>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Global Validity Dates Section - FIXED: Now shows dates correctly */}
                                                    <div className="row mb-4">
                                                        <div className="col-12">
                                                            <div className="card border-info">
                                                                <div className="card-header bg-info bg-opacity-10">
                                                                    <h6 className="mb-0 text-info">
                                                                        <i className="bi bi-calendar-range me-2"></i>
                                                                        Validity Dates (Applies to all rooms)
                                                                    </h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    <div className="row align-items-end">
                                                                        <div className="col-md-5">
                                                                            <label className="form-label">Valid From</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={globalValidFrom}
                                                                                onChange={handleGlobalValidFromChange}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-5">
                                                                            <label className="form-label">Valid To</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={globalValidTo}
                                                                                onChange={handleGlobalValidToChange}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-2">
                                                                            {globalValidFrom && globalValidTo && (
                                                                                <div className="text-success">
                                                                                    <i className="bi bi-check-circle-fill me-1"></i>
                                                                                    Set for all rooms
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <small className="text-muted d-block mt-2">
                                                                        These dates will be applied to all room types
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Tabs Navigation */}
                                                    <div className="row mb-4">
                                                        <div className="col-12">
                                                            <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                                                                {tabs.map((tab) => (
                                                                    <li key={tab.id} className="nav-item" role="presentation">
                                                                        <button
                                                                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                                                            onClick={() => setActiveTab(tab.id)}
                                                                            type="button"
                                                                        >
                                                                            <i className={`${tab.icon} me-1`}></i>
                                                                            {tab.label}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Tab Content */}
                                                    <div className="tab-content">
                                                        {/* Room Details Tab - UPDATED: Removed date fields from add form and table */}
                                                        {activeTab === "roomDetails" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-bed me-2"></i>
                                                                            Room Details - {selectedHotel.hotelName}
                                                                        </h6>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => setShowAddRoomForm(!showAddRoomForm)}
                                                                        >
                                                                            <i className="bi bi-plus me-1"></i>
                                                                            Add Room
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    <div className="card-body p-0">
                                                                        {/* Add Room Form - UPDATED: Removed date fields */}
                                                                        {showAddRoomForm && (
                                                                            <div className="card border-info m-3">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title text-info mb-3">Add New Room</h6>
                                                                                    <div className="row g-3 align-items-end">
                                                                                        {/* Auto-complete Room Type Field */}
                                                                                        {renderRoomTypeField()}
                                                                                        
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Meal Plan *</label>
                                                                                            <select
                                                                                                className="form-select"
                                                                                                name="mealPlan"
                                                                                                value={newRoom.mealPlan}
                                                                                                onChange={handleNewRoomChange}
                                                                                                required
                                                                                            >
                                                                                                {mealPlanOptions.map((option, index) => (
                                                                                                    <option key={index} value={option.value}>
                                                                                                        {option.label}
                                                                                                    </option>
                                                                                                ))}
                                                                                            </select>
                                                                                        </div>
                                                                                        <div className="col-md-3">
                                                                                            <label className="form-label">Price *</label>
                                                                                            <div className="input-group">
                                                                                                <input
                                                                                                    type="number"
                                                                                                    className="form-control"
                                                                                                    name="price"
                                                                                                    value={newRoom.price}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    placeholder="Price"
                                                                                                    required
                                                                                                    min="0"
                                                                                                />
                                                                                                <select
                                                                                                    className="form-select"
                                                                                                    name="currency"
                                                                                                    value={newRoom.currency}
                                                                                                    onChange={handleNewRoomChange}
                                                                                                    style={{ width: '80px' }}
                                                                                                >
                                                                                                    {currencyOptions.map((currency) => (
                                                                                                        <option key={currency.code} value={currency.code}>
                                                                                                            {currency.code}
                                                                                                        </option>
                                                                                                    ))}
                                                                                                </select>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="col-md-3">
                                                                                            <div className="row g-2">
                                                                                                <div className="col-md-6">
                                                                                                    <div className="form-check mb-2">
                                                                                                        <input
                                                                                                            className="form-check-input"
                                                                                                            type="checkbox"
                                                                                                            name="extraBed"
                                                                                                            checked={newRoom.extraBed}
                                                                                                            onChange={handleNewRoomChange}
                                                                                                            id="extraBed"
                                                                                                        />
                                                                                                        <label className="form-check-label ms-2" htmlFor="extraBed">
                                                                                                            Extra Bed
                                                                                                        </label>
                                                                                                    </div>
                                                                                                    {newRoom.extraBed && (
                                                                                                        <div className="input-group">
                                                                                                            <input
                                                                                                                type="number"
                                                                                                                className="form-control"
                                                                                                                name="extraBedPrice"
                                                                                                                value={newRoom.extraBedPrice}
                                                                                                                onChange={handleNewRoomChange}
                                                                                                                placeholder="Enter price"
                                                                                                                min="0"
                                                                                                                required
                                                                                                            />
                                                                                                            <span className="input-group-text">
                                                                                                                {getCurrencySymbol(newRoom.currency)}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                                <div className="col-md-6">
                                                                                                    <div className="form-check mb-2">
                                                                                                        <input
                                                                                                            className="form-check-input"
                                                                                                            type="checkbox"
                                                                                                            name="childWithoutBed"
                                                                                                            checked={newRoom.childWithoutBed}
                                                                                                            onChange={handleNewRoomChange}
                                                                                                            id="childWithoutBed"
                                                                                                        />
                                                                                                        <label className="form-check-label ms-2" htmlFor="childWithoutBed">
                                                                                                            Child w/o bed
                                                                                                        </label>
                                                                                                    </div>
                                                                                                    {newRoom.childWithoutBed && (
                                                                                                        <div className="input-group">
                                                                                                            <input
                                                                                                                type="number"
                                                                                                                className="form-control"
                                                                                                                name="childWithoutBedPrice"
                                                                                                                value={newRoom.childWithoutBedPrice}
                                                                                                                onChange={handleNewRoomChange}
                                                                                                                placeholder="Enter price"
                                                                                                                min="0"
                                                                                                                required
                                                                                                            />
                                                                                                            <span className="input-group-text">
                                                                                                                {getCurrencySymbol(newRoom.currency)}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="col-12 d-flex gap-2 mt-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-primary"
                                                                                                onClick={handleAddRoom}
                                                                                            >
                                                                                                <i className="bi bi-check me-1"></i>
                                                                                                Add Room
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-danger"
                                                                                                onClick={() => setShowAddRoomForm(false)}
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {/* Room Details Table - UPDATED: Removed date columns */}
                                                                        {roomDetails.length > 0 ? (
                                                                            <>
                                                                                <div className="table-responsive">
                                                                                    <table className="table table-hover mb-0">
                                                                                        <thead className="table-light">
                                                                                            <tr>
                                                                                                <th>Room Type</th>
                                                                                                <th>Meal Plan</th>
                                                                                                <th>Price</th>
                                                                                                <th>Extra Bed</th>
                                                                                                <th>Child w/o Bed</th>
                                                                                                <th>Actions</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {roomDetails.map((room) => {
                                                                                                const currencySymbol = getCurrencySymbol(room.currency);
                                                                                                const mealPlanLabel = mealPlanOptions.find(mp => mp.value === room.mealPlan)?.label || room.mealPlan;
                                                                                                
                                                                                                return (
                                                                                                    <tr key={room.id}>
                                                                                                        <td>{room.roomType}</td>
                                                                                                        <td>{mealPlanLabel}</td>
                                                                                                        <td>
                                                                                                            <strong>{currencySymbol}{room.price}</strong>
                                                                                                            <small className="text-muted ms-1">({room.currency})</small>
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            {room.extraBedPrice && room.extraBedPrice ? (
                                                                                                                <span className="badge bg-success">
                                                                                                                    {currencySymbol}{room.extraBedPrice}
                                                                                                                </span>
                                                                                                            ) : (
                                                                                                                <span className="badge bg-secondary">No</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            {room.childWithoutBedPrice && room.childWithoutBedPrice ? (
                                                                                                                <span className="badge bg-info">
                                                                                                                    {currencySymbol}{room.childWithoutBedPrice}
                                                                                                                </span>
                                                                                                            ) : (
                                                                                                                <span className="badge bg-secondary">No</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            <div className="btn-group btn-group-sm">
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="btn btn-primary"
                                                                                                                    onClick={() => handleEditRoom(room)}
                                                                                                                >
                                                                                                                    <i className="bi bi-pencil"></i>
                                                                                                                </button>
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="btn btn-danger"
                                                                                                                    onClick={() => handleDeleteRoom(room.id)}
                                                                                                                >
                                                                                                                    <i className="bi bi-trash"></i>
                                                                                                                </button>
                                                                                                            </div>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                                
                                                                                {/* Show global validity dates note */}
                                                                                {globalValidFrom && globalValidTo && (
                                                                                    <div className="alert alert-info mt-3 mb-0">
                                                                                        <i className="bi bi-calendar-check me-2"></i>
                                                                                        <strong>Validity dates applied to all rooms:</strong> {formatDate(globalValidFrom)} to {formatDate(globalValidTo)}
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No room details added yet</h6>
                                                                                <p className="text-muted small">Click "Add Room" to add room details for this property</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Amenities Tab */}
                                                        {activeTab === "amenities" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-wifi me-2"></i>
                                                                            Amenities - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Amenity Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Amenity Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newAmenityPoint}
                                                                                            onChange={(e) => setNewAmenityPoint(e.target.value)}
                                                                                            placeholder="Enter amenity point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddAmenityPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Amenities Points Display */}
                                                                        {amenities.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Amenities Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {amenities.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditAmenityPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteAmenityPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No amenities added yet</h6>
                                                                                <p className="text-muted small">Add points above to create amenities list</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Terms & Conditions Tab */}
                                                        {activeTab === "terms" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-file-text me-2"></i>
                                                                            Terms & Conditions - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Term Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Term & Condition Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newTermPoint}
                                                                                            onChange={(e) => setNewTermPoint(e.target.value)}
                                                                                            placeholder="Enter term & condition point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddTermPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Terms & Conditions Points Display */}
                                                                        {termsAndConditions.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Terms & Conditions Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {termsAndConditions.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditTermPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteTermPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No terms & conditions added yet</h6>
                                                                                <p className="text-muted small">Add points above to create terms & conditions</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Cancellation Policy Tab */}
                                                        {activeTab === "cancellation" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-calendar-event me-2"></i>
                                                                            Cancellation Policy - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Cancellation Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Cancellation Policy Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newCancellationPoint}
                                                                                            onChange={(e) => setNewCancellationPoint(e.target.value)}
                                                                                            placeholder="Enter cancellation policy point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddCancellationPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Cancellation Policy Points Display */}
                                                                        {cancellationPolicy.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Cancellation Policy Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {cancellationPolicy.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditCancellationPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteCancellationPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No cancellation policy added yet</h6>
                                                                                <p className="text-muted small">Add points above to create cancellation policy</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Child Policy Tab */}
                                                        {activeTab === "childPolicy" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-person me-2"></i>
                                                                            Child Policy - {selectedHotel.hotelName}
                                                                        </h6>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Child Policy Point Form */}
                                                                        <div className="card border-info mb-3">
                                                                            <div className="card-body">
                                                                                <h6 className="card-title text-info mb-3">Add New Child Policy Point</h6>
                                                                                <div className="row g-3">
                                                                                    <div className="col-md-10">
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control"
                                                                                            value={newChildPolicyPoint}
                                                                                            onChange={(e) => setNewChildPolicyPoint(e.target.value)}
                                                                                            placeholder="Enter child policy point..."
                                                                                        />
                                                                                    </div>
                                                                                    <div className="col-md-2">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-primary w-100"
                                                                                            onClick={handleAddChildPolicyPoint}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            Add Point
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Child Policy Points Display */}
                                                                        {childPolicy.length > 0 ? (
                                                                            <div className="card">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title mb-3">Child Policy Points</h6>
                                                                                    <ol className="list-group list-group-numbered">
                                                                                        {childPolicy.map((point) => (
                                                                                            <li key={point.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                                                                <div className="ms-2 me-auto">
                                                                                                    <div className="fw-medium">{point.point}</div>
                                                                                                </div>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditChildPolicyPoint(point)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteChildPolicyPoint(point.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ol>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No child policy added yet</h6>
                                                                                <p className="text-muted small">Add points above to create child policy</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Bank Details Tab */}
                                                        {activeTab === "bankDetails" && (
                                                            <div className="tab-pane fade show active">
                                                                <div className="card border-primary">
                                                                    <div className="card-header bg-primary bg-opacity-10 d-flex justify-content-between align-items-center">
                                                                        <h6 className="mb-0">
                                                                            <i className="bi bi-bank me-2"></i>
                                                                            Bank Details - {selectedHotel.hotelName}
                                                                        </h6>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => setShowAddBankForm(!showAddBankForm)}
                                                                        >
                                                                            <i className="bi bi-plus me-1"></i>
                                                                            Add Bank Account
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    <div className="card-body">
                                                                        {/* Add Bank Form */}
                                                                        {showAddBankForm && (
                                                                            <div className="card border-info mb-3">
                                                                                <div className="card-body">
                                                                                    <h6 className="card-title text-info mb-3">Add Bank Account</h6>
                                                                                    <div className="row g-3">
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">Bank Name *</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="bankName"
                                                                                                value={newBankDetail.bankName}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., State Bank of India"
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">Account Number *</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="accountNumber"
                                                                                                value={newBankDetail.accountNumber}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., 1234567890"
                                                                                                required
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-md-4">
                                                                                            <label className="form-label">SWIFT/IFSC Code</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control"
                                                                                                name="swiftIfscCode"
                                                                                                value={newBankDetail.swiftIfscCode}
                                                                                                onChange={handleBankDetailChange}
                                                                                                placeholder="e.g., SBIN0001234 or SBININBB123"
                                                                                            />
                                                                                        </div>
                                                                                        <div className="col-12 d-flex gap-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-primary"
                                                                                                onClick={handleAddBankDetail}
                                                                                            >
                                                                                                <i className="bi bi-check me-1"></i>
                                                                                                Add Account
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-danger"
                                                                                                onClick={() => setShowAddBankForm(false)}
                                                                                            >
                                                                                                Cancel
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {/* Bank Details Display */}
                                                                        {bankDetails.length > 0 ? (
                                                                            <div className="row">
                                                                                {bankDetails.map((bank) => (
                                                                                    <div key={bank.id} className="col-md-6 mb-3">
                                                                                        <div className="card h-100">
                                                                                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                                                                                <h6 className="mb-0">{bank.bankName}</h6>
                                                                                                <div className="btn-group btn-group-sm">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-primary"
                                                                                                        onClick={() => handleEditBankDetail(bank)}
                                                                                                    >
                                                                                                        <i className="bi bi-pencil"></i>
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-danger"
                                                                                                        onClick={() => handleDeleteBankDetail(bank.id)}
                                                                                                    >
                                                                                                        <i className="bi bi-trash"></i>
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="card-body">
                                                                                                <div className="mb-2">
                                                                                                    <small className="text-muted">Account Number</small>
                                                                                                    <p className="mb-1 fw-medium">{bank.accountNumber}</p>
                                                                                                </div>
                                                                                                {bank.swiftIfscCode && (
                                                                                                    <div className="mb-2">
                                                                                                        <small className="text-muted">SWIFT/IFSC Code</small>
                                                                                                        <p className="mb-1 fw-medium">{bank.swiftIfscCode}</p>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-center py-5">
                                                                                <h6 className="text-muted">No bank details added yet</h6>
                                                                                <p className="text-muted small">Click "Add Bank Account" to add bank details for this property</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Render the Preview Modal */}
            {showPreviewModal && <PreviewModal />}
            
            {/* Custom CSS */}
            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    padding: 20px;
                }
                
                .modal-container {
                    background-color: white;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 1200px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
                }
                
                .nav-tabs-custom .nav-link {
                    color: #495057;
                    border: 1px solid transparent;
                    border-top-left-radius: 0.25rem;
                    border-top-right-radius: 0.25rem;
                    padding: 0.75rem 1.25rem;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }
                
                .nav-tabs-custom .nav-link:hover {
                    border-color: #e9ecef #e9ecef #dee2e6;
                    color: #0d6efd;
                }
                
                .nav-tabs-custom .nav-link.active {
                    color: #0d6efd;
                    background-color: #fff;
                    border-color: #dee2e6 #dee2e6 #fff;
                    border-bottom: 3px solid #0d6efd;
                }
                
                .tab-content {
                    padding-top: 1rem;
                }
                
                .hotel-card:hover {
                    border-color: #0d6efd !important;
                }
                
                .dropdown-item:hover {
                    background-color: #f8f9fa;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                .position-relative {
                    position: relative;
                }
                
                .position-absolute {
                    position: absolute;
                }
                
                @media (max-width: 768px) {
                    .nav-tabs-custom .nav-link {
                        padding: 0.5rem;
                        font-size: 0.85rem;
                    }
                    
                    .room-form-row .col-md-2,
                    .room-form-row .col-md-3 {
                        margin-bottom: 10px;
                    }
                    
                    .modal-container {
                        margin: 10px;
                        max-height: 95vh;
                    }
                }
            `}</style>
        </div>
    );
};

export default HotelDMCProperties;